import { RichTextDisplay } from "@/components/RichTextEditor";

/**
 * Compact 5e/2024-style stat block for Companions and Summons.
 * Visual reference: cream background, brown framed card, two-column layout
 * (AC/Init, HP, Speed, ability table on the left; Skills/Senses/Languages/CR
 * and Actions on the right). See user-supplied mock (Badger).
 */

const abilityMod = (score) => {
  const s = parseInt(score, 10);
  if (Number.isNaN(s)) return 0;
  return Math.floor((s - 10) / 2);
};
const fmt = (n) => (n >= 0 ? `+${n}` : `${n}`);

const Cell = ({ label, value, mono = false }) => (
  <p className="text-sm leading-snug" data-testid={`comp-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className="font-bold" style={{ color: "#3d2519" }}>{label}</span>{" "}
    <span className={mono ? "tabular-nums" : ""} style={{ color: "#4b3724" }}>{value}</span>
  </p>
);

export const CompanionStatBlock = ({ entity, getImageUrl }) => {
  if (!entity) return null;
  const e = entity;
  const headerLine = [e.size, e.creature_type || e.race, e.alignment].filter(Boolean).join(", ");

  const sheet = e.character_sheet || {};
  const abilities = sheet.abilities || {
    STR: e.str_score ?? 10, DEX: e.dex_score ?? 10, CON: e.con_score ?? 10,
    INT: e.int_score ?? 10, WIS: e.wis_score ?? 10, CHA: e.cha_score ?? 10,
  };

  const savingProfs = sheet.saving_throws || [];
  const profBonus = sheet.proficiency_bonus ?? 2;
  const saveMod = (ab) => abilityMod(abilities[ab] ?? 10) + (savingProfs.includes(ab) ? profBonus : 0);
  const initBonus = abilityMod(abilities.DEX ?? 10);

  const rows = [
    ["STR", "INT"],
    ["DEX", "WIS"],
    ["CON", "CHA"],
  ];

  const actions = e.actions || [];
  const bonus_actions = e.bonus_actions || [];
  const reactions = e.reactions || [];
  const traits = e.traits || [];

  return (
    <div
      data-testid="companion-stat-block"
      className="rounded-md max-w-3xl"
      style={{
        background: "#faf5e8",
        border: "1px solid #c9b27a",
        color: "#3d2519",
        boxShadow: "0 1px 0 #efe2c0 inset, 0 8px 24px rgba(0,0,0,0.18)",
      }}
    >
      <div className="p-5">
        {/* Portrait optional */}
        {getImageUrl && getImageUrl(e) && (
          <div className="float-right ml-4 mb-2 w-28 h-28 rounded-sm overflow-hidden border" style={{ borderColor: "#c9b27a" }}>
            <img src={getImageUrl(e)} alt={e.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
          </div>
        )}

        {/* Name */}
        <h2 className="font-serif text-3xl tracking-wide leading-none" style={{ color: "#7b1f1f" }} data-testid="comp-name">{e.name}</h2>
        {headerLine && (
          <p className="text-sm italic mt-1" style={{ color: "#8b7355" }} data-testid="comp-type-line">{headerLine}</p>
        )}

        <div className="my-3 border-t" style={{ borderColor: "#c9b27a" }} />

        {/* Two-column body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          {/* LEFT COLUMN */}
          <div className="space-y-2">
            <Cell label="AC" value={`${e.armor_class || 10}   Initiative ${fmt(initBonus)} (${10 + initBonus})`} mono />
            <Cell label="HP" value={e.hit_points || "—"} />
            <Cell label="Speed" value={e.speed || "30 ft."} />

            {/* Abilities table */}
            <div className="mt-3 rounded-sm overflow-hidden" data-testid="comp-abilities" style={{ border: "1px solid #c9b27a" }}>
              <div className="grid grid-cols-8 text-[10px] uppercase tracking-wider px-2 py-1" style={{ background: "#efe2c0", color: "#7a5a35" }}>
                <span className="col-span-2" />
                <span className="text-center">Mod</span>
                <span className="text-center">Save</span>
                <span className="col-span-2" />
                <span className="text-center">Mod</span>
                <span className="text-center">Save</span>
              </div>
              {rows.map(([a, b], idx) => {
                const aMod = abilityMod(abilities[a] ?? 10);
                const aSave = saveMod(a);
                const bMod = abilityMod(abilities[b] ?? 10);
                const bSave = saveMod(b);
                const stripe = idx % 2 === 0 ? "#faf5e8" : "#f3e9cf";
                return (
                  <div key={`${a}-${b}`} className="grid grid-cols-8 text-sm py-1.5 px-2" style={{ background: stripe }} data-testid={`comp-row-${a.toLowerCase()}-${b.toLowerCase()}`}>
                    <span className="font-bold col-span-1">{a}</span>
                    <span className="tabular-nums">{abilities[a] ?? 10}</span>
                    <span className="text-center tabular-nums">{fmt(aMod)}</span>
                    <span className="text-center tabular-nums" style={{ color: savingProfs.includes(a) ? "#7b1f1f" : "#4b3724" }}>{fmt(aSave)}</span>
                    <span className="font-bold col-span-1">{b}</span>
                    <span className="tabular-nums">{abilities[b] ?? 10}</span>
                    <span className="text-center tabular-nums">{fmt(bMod)}</span>
                    <span className="text-center tabular-nums" style={{ color: savingProfs.includes(b) ? "#7b1f1f" : "#4b3724" }}>{fmt(bSave)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT COLUMN — Skills / Resistances / Senses / Languages / CR */}
          <div className="mt-4 md:mt-0 space-y-2">
            {e.skills && <Cell label="Skills" value={e.skills} />}
            {e.damage_resistances && <Cell label="Resistances" value={e.damage_resistances} />}
            {e.damage_immunities && <Cell label="Immunities" value={e.damage_immunities} />}
            {e.condition_immunities && <Cell label="Condition Immunities" value={e.condition_immunities} />}
            {e.senses && <Cell label="Senses" value={e.senses} />}
            <Cell label="Languages" value={e.languages || "None"} />
            {e.challenge_rating && (
              <Cell label="CR" value={`${e.challenge_rating}${e.xp ? ` (XP ${e.xp}; PB +${profBonus})` : ` (PB +${profBonus})`}`} />
            )}
          </div>
        </div>

        {/* Traits */}
        {traits.length > 0 && (
          <section className="mt-5" data-testid="comp-section-traits">
            <h3 className="font-serif text-xl" style={{ color: "#7b1f1f" }}>Traits</h3>
            <div className="border-t mb-2" style={{ borderColor: "#c9b27a" }} />
            <div className="space-y-2">
              {traits.map((t, i) => (
                <p key={i} className="text-sm leading-relaxed"><strong className="italic">{t.name}.</strong> {t.description}</p>
              ))}
            </div>
          </section>
        )}

        {/* Actions */}
        {actions.length > 0 && (
          <section className="mt-5" data-testid="comp-section-actions">
            <h3 className="font-serif text-xl" style={{ color: "#7b1f1f" }}>Actions</h3>
            <div className="border-t mb-2" style={{ borderColor: "#c9b27a" }} />
            <div className="space-y-2">
              {actions.map((a, i) => (
                <p key={i} className="text-sm leading-relaxed"><strong className="italic">{a.name}.</strong> {a.description}</p>
              ))}
            </div>
          </section>
        )}

        {/* Bonus Actions */}
        {bonus_actions.length > 0 && (
          <section className="mt-5" data-testid="comp-section-bonus-actions">
            <h3 className="font-serif text-xl" style={{ color: "#7b1f1f" }}>Bonus Actions</h3>
            <div className="border-t mb-2" style={{ borderColor: "#c9b27a" }} />
            <div className="space-y-2">
              {bonus_actions.map((a, i) => (
                <p key={i} className="text-sm leading-relaxed"><strong className="italic">{a.name}.</strong> {a.description}</p>
              ))}
            </div>
          </section>
        )}

        {/* Reactions */}
        {reactions.length > 0 && (
          <section className="mt-5" data-testid="comp-section-reactions">
            <h3 className="font-serif text-xl" style={{ color: "#7b1f1f" }}>Reactions</h3>
            <div className="border-t mb-2" style={{ borderColor: "#c9b27a" }} />
            <div className="space-y-2">
              {reactions.map((a, i) => (
                <p key={i} className="text-sm leading-relaxed"><strong className="italic">{a.name}.</strong> {a.description}</p>
              ))}
            </div>
          </section>
        )}

        {/* Description / Lore */}
        {e.description && (
          <section className="mt-5" data-testid="comp-section-description">
            <h3 className="font-serif text-xl" style={{ color: "#7b1f1f" }}>Description</h3>
            <div className="border-t mb-2" style={{ borderColor: "#c9b27a" }} />
            <div className="text-sm leading-relaxed">
              <RichTextDisplay content={e.description} />
            </div>
          </section>
        )}

        <div style={{ clear: "both" }} />
      </div>
    </div>
  );
};

export default CompanionStatBlock;
